import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import StaffLink from '../../components/shared/StaffLink';
import { PlusIcon, XMarkIcon, LockClosedIcon, ExclamationTriangleIcon, DocumentArrowDownIcon, ClockIcon } from '@heroicons/react/24/outline';
import { DATE_FLOOR } from '../../lib/dateBounds';

const recordTypes = [
  { value: 'verbal_warning', label: 'Verbal Warning' },
  { value: 'written_warning', label: 'Written Warning' },
  { value: 'performance_improvement_plan', label: 'Performance Improvement Plan' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'termination', label: 'Termination' },
  { value: 'director_action', label: 'Director Action' },
];

export default function DisciplinaryPage() {
  const { user, isDirector, isManagement } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [detail, setDetail] = useState(null);
  const { register, handleSubmit, reset } = useForm();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [recRes, staffRes] = await Promise.all([
        api.get('/disciplinary'),
        api.get('/staff'),
      ]);
      setRecords(recRes.data);
      setStaffList(staffRes.data);
    } catch (err) {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (formData) => {
    try {
      await api.post('/disciplinary', {
        ...formData,
        staff_user_id: parseInt(formData.staff_user_id),
      });
      toast.success('Record created');
      setShowForm(false);
      reset();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create');
    }
  };

  const acknowledge = async (id, response) => {
    try {
      await api.patch(`/disciplinary/${id}/acknowledge`, { staff_response: response });
      toast.success('Record acknowledged');
      loadData();
    } catch (err) {
      toast.error('Failed to acknowledge');
    }
  };

  const directorSign = async (id) => {
    try {
      await api.patch(`/disciplinary/${id}/director-sign`);
      toast.success('Record signed and locked');
      loadData();
    } catch (err) {
      toast.error('Failed to sign');
    }
  };

  const generatePDF = async (id) => {
    try {
      const { data } = await api.post(`/disciplinary/${id}/generate-pdf`);
      window.open(data.pdf_url, '_blank');
      toast.success('PDF ready');
      loadData();
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  return (
    <div className="animate-slide-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Disciplinary Records</h1>
          <p className="text-gray-500 text-sm">{records.length} record{records.length !== 1 ? 's' : ''}</p>
        </div>
        {isManagement && (
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 transition-colors font-medium shadow-lg shadow-orange-200/50">
            <PlusIcon className="w-5 h-5" /> New Record
          </button>
        )}
      </div>

      {records.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <ExclamationTriangleIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No disciplinary records</p>
        </div>
      ) : (
        <div className="space-y-4">
          {records.map((rec) => (
            <div key={rec.id} className={`bg-white rounded-2xl border p-5 ${rec.is_locked ? 'border-gray-300 bg-gray-50/50' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">
                      <StaffLink id={rec.staff_user_id} name={rec.staff_name} />
                    </p>
                    {rec.is_locked && <LockClosedIcon className="w-4 h-4 text-gray-400" title="Locked" />}
                    {rec.triggered_by?.startsWith('attendance_threshold') && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <ClockIcon className="w-3 h-3" /> Auto from attendance
                      </span>
                    )}
                    {rec.discipline_type && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full uppercase">
                        {rec.discipline_type}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {new Date(rec.incident_date).toLocaleDateString()} · Issued by: {rec.issued_by_name}
                  </p>
                </div>
                <StatusBadge status={rec.record_type} />
              </div>
              <p className="text-sm text-gray-700 mb-2">{rec.description}</p>
              <p className="text-sm text-gray-600"><span className="font-medium">Action:</span> {rec.action_taken}</p>

              {detail === rec.id && (
                <div className="border-t border-gray-100 pt-3 mt-3 space-y-2 text-sm">
                  {rec.recommendations_referrals && <div><span className="font-medium text-gray-700">Recommendations / Referrals:</span> <span className="text-gray-600 whitespace-pre-wrap">{rec.recommendations_referrals}</span></div>}
                  {rec.outcome && <div><span className="font-medium text-gray-700">Outcome:</span> <span className="text-gray-600">{rec.outcome}</span></div>}
                  {rec.staff_response && <div><span className="font-medium text-gray-700">Staff response:</span> <span className="text-gray-600">{rec.staff_response}</span></div>}
                  {rec.follow_up_date && <div><span className="font-medium text-gray-700">Follow-up:</span> <span className="text-gray-600">{new Date(rec.follow_up_date).toLocaleDateString()}</span></div>}
                  {rec.director_signed_by_name && <div><span className="font-medium text-gray-700">Director signed:</span> <span className="text-gray-600">{rec.director_signed_by_name} on {new Date(rec.director_signed_at).toLocaleDateString()}</span></div>}
                  {rec.pdf_url && (
                    <div>
                      <a href={rec.pdf_url} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline text-sm inline-flex items-center gap-1">
                        <DocumentArrowDownIcon className="w-4 h-4" /> View PDF
                      </a>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-3 mt-3">
                <button onClick={() => setDetail(detail === rec.id ? null : rec.id)} className="text-sm text-gray-500 hover:text-gray-700">
                  {detail === rec.id ? 'Hide details' : 'View details'}
                </button>
                {!rec.is_locked && rec.staff_user_id === user.id && !rec.staff_acknowledged_at && (
                  <button onClick={() => acknowledge(rec.id, '')} className="text-sm text-orange-500 hover:text-orange-600 font-medium">
                    Acknowledge
                  </button>
                )}
                {!rec.is_locked && isDirector && rec.staff_acknowledged_at && !rec.director_signed_at &&
                  ['written_warning', 'performance_improvement_plan', 'suspension', 'termination'].includes(rec.record_type) && (
                  <button onClick={() => directorSign(rec.id)} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                    Director Sign & Lock
                  </button>
                )}
                {isManagement && (
                  <button onClick={() => generatePDF(rec.id)} className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1">
                    <DocumentArrowDownIcon className="w-4 h-4" /> Generate PDF
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Record Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold">New Disciplinary Record</h2>
              <button onClick={() => { setShowForm(false); reset(); }}><XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member</label>
                <select {...register('staff_user_id', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none bg-white">
                  <option value="">Select staff</option>
                  {staffList.filter(s => s.id !== user.id).map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Record Type</label>
                <select {...register('record_type', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none bg-white">
                  <option value="">Select type</option>
                  {recordTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Verbal or Written?</label>
                <div className="flex gap-2">
                  {['verbal', 'written', 'both'].map(t => (
                    <label key={t} className="flex-1">
                      <input type="radio" {...register('discipline_type')} value={t} className="sr-only peer" />
                      <div className="border border-gray-200 rounded-xl px-3 py-2 text-center text-sm cursor-pointer peer-checked:border-orange-500 peer-checked:bg-orange-50 peer-checked:text-orange-700">
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Incident Date</label>
                <input type="date" min={DATE_FLOOR} {...register('incident_date', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea {...register('description', { required: true })} rows={3} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action Plan</label>
                <textarea {...register('action_taken')} rows={2} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recommendations / Referrals Provided</label>
                <textarea {...register('recommendations_referrals')} rows={2} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
                <input type="date" min={DATE_FLOOR} {...register('follow_up_date')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-orange-500 text-white font-semibold py-2.5 rounded-xl hover:bg-orange-600 transition-colors">Create Record</button>
                <button type="button" onClick={() => { setShowForm(false); reset(); }} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
